// useSpeechRecognition Hook - Voice input for chat
// Uses @capacitor-community/speech-recognition on native iOS/Android,
// falls back to the Web Speech API on web.
import { useState, useCallback, useRef, useEffect } from 'react';
import { isNative } from '../utils/platform';

// TypeScript types for Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

export interface UseSpeechRecognitionReturn {
  isListening: boolean;
  transcript: string;
  interimTranscript: string;
  error: string | null;
  isSupported: boolean;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
}

export function useSpeechRecognition(lang: string = 'en-US'): UseSpeechRecognitionReturn {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(true);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = useRef('');

  // --- Native (Capacitor) path ---
  useEffect(() => {
    if (!isNative()) return;

    import('@capacitor-community/speech-recognition').then(({ SpeechRecognition }) => {
      SpeechRecognition.available().then(({ available }) => {
        if (!available) {
          setIsSupported(false);
          setError('Speech recognition is not available on this device');
        }
      });
    }).catch(() => {
      setIsSupported(false);
    });
  }, []);

  // --- Web path ---
  useEffect(() => {
    if (isNative()) return;

    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognitionAPI) {
      setIsSupported(false);
      setError('Speech recognition is not supported in this browser');
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
      finalTranscriptRef.current = transcript;
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (final) {
        finalTranscriptRef.current += final;
        setTranscript(finalTranscriptRef.current);
      }
      setInterimTranscript(interim);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      let errorMessage = 'Speech recognition error';
      switch (event.error) {
        case 'no-speech': errorMessage = 'No speech detected. Please try again.'; break;
        case 'aborted': errorMessage = 'Speech recognition was aborted.'; break;
        case 'audio-capture': errorMessage = 'No microphone found. Please check your microphone.'; break;
        case 'not-allowed': errorMessage = 'Microphone permission denied. Please allow microphone access.'; break;
        case 'network': errorMessage = 'Network error. Please check your connection.'; break;
        default: errorMessage = `Error: ${event.error}`;
      }
      setError(errorMessage);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
    };

    recognitionRef.current = recognition;

    return () => {
      recognitionRef.current?.abort();
    };
  // Intentionally reads the latest transcript without re-subscribing to recognition events when it changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const startListening = useCallback(() => {
    setError(null);

    if (isNative()) {
      import('@capacitor-community/speech-recognition').then(({ SpeechRecognition }) => {
        SpeechRecognition.requestPermissions().then(() => {
          SpeechRecognition.start({
            language: lang,
            maxResults: 1,
            partialResults: true,
            popup: false,
          });

          SpeechRecognition.addListener('partialResults', (data: { matches: string[] }) => {
            if (data.matches?.[0]) {
              setInterimTranscript(data.matches[0]);
            }
          });

          setIsListening(true);
        }).catch(() => {
          setError('Microphone permission denied. Please allow microphone access.');
        });
      }).catch(() => {
        setError('Speech recognition plugin not available');
      });
      return;
    }

    if (!recognitionRef.current) {
      setError('Speech recognition is not supported');
      return;
    }

    try {
      recognitionRef.current.start();
    } catch (err: any) {
      if (err.name === 'InvalidStateError') {
        recognitionRef.current.stop();
        setTimeout(() => recognitionRef.current?.start(), 100);
      } else {
        setError('Failed to start speech recognition');
      }
    }
  }, [lang]);

  const stopListening = useCallback(() => {
    if (isNative()) {
      import('@capacitor-community/speech-recognition').then(({ SpeechRecognition }) => {
        SpeechRecognition.stop().then(() => {
          // Move any interim result to final transcript
          setTranscript(prev => {
            const combined = prev + interimTranscript;
            finalTranscriptRef.current = combined;
            return combined;
          });
          setInterimTranscript('');
          setIsListening(false);
        });
      }).catch(() => {});
      return;
    }

    recognitionRef.current?.stop();
  }, [interimTranscript]);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    finalTranscriptRef.current = '';
  }, []);

  return {
    isListening,
    transcript,
    interimTranscript,
    error,
    isSupported,
    startListening,
    stopListening,
    resetTranscript,
  };
}

export default useSpeechRecognition;
